import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Minimize2 } from 'lucide-react';
import { posthog } from 'posthog-js';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import DeviceSelection from './deviceSelection';
import RecorderControls from './RecorderControls';
import Notepad from './Notepad';
import { RootState } from '../store';
import PopupRecorder from './PopupRecorder';
import { setUIMode } from '../recorderSlice';
import { Z_INDEX } from '../../constants/zIndex';
import SkipDeviceSelection from './skipDeviceSelection';
import { streams } from './utils/streamUtils';
import {
  AudioLevelDetector,
  AudioLevelDetectorConfig,
} from './utils/audioLevelDetector';
import useUploadPulse from '../hooks/useUploadPulse';
import type { AudioStatus } from './RecorderControls/AudioStatusBadge';

const RecorderComponent = () => {
  const dispatch = useDispatch();
  const { status, controls, settings, selectedDevices } = useSelector(
    (state: RootState) => state.recorder,
  );
  const [showNotes, setShowNotes] = useState(() => {
    const savedValue = localStorage.getItem('notepad-visible');
    return savedValue ? JSON.parse(savedValue) : true;
  });

  // Microphone audio detection state
  const [micAudioWarning, setMicAudioWarning] = useState(false);
  const [micEverDetected, setMicEverDetected] = useState(false);
  const micDetector = useRef<AudioLevelDetector | null>(null);
  const micAnalyser = useRef<AnalyserNode | null>(null);
  const hasMicDetectedRef = useRef<boolean>(false);

  // System/Tab audio detection state
  const [systemAudioWarning, setSystemAudioWarning] = useState(false);
  const [systemEverDetected, setSystemEverDetected] = useState(false);
  const [tabAudioActive, setTabAudioActive] = useState(false);
  const systemDetector = useRef<AudioLevelDetector | null>(null);
  const systemAnalyser = useRef<AnalyserNode | null>(null);
  const hasSystemDetectedRef = useRef<boolean>(false);

  // Shared AudioContext
  const audioContext = useRef<AudioContext | null>(null);

  // Track whether mic detector was set up for this recording session
  // (separate from whether mic is currently enabled/muted)
  const micDetectorSetUp = useRef<boolean>(false);

  // Recording analytics tracking
  const recordingStartTime = useRef<number | null>(null);

  const isBackdropInvisible = status === 'stopped';
  const showDeviceSelection = status === 'initializing' || status === 'ready';
  const isSaving = status === 'saving';
  const isRecording = status === 'recording';
  const { uiMode, isMountedFromPlugin } = controls;
  const { webCam, screen } = settings?.videoSettings || {};
  const { microphoneAudio } = settings?.audioSettings || {};

  // Microphone audio detection callbacks
  const handleMicSilent = () => {
    console.log('Microphone silent - no audio for 5 seconds');
    if (isRecording && !hasMicDetectedRef.current) {
      setMicAudioWarning(true);
    }
  };

  const handleMicAudioDetected = () => {
    hasMicDetectedRef.current = true;
    setMicEverDetected(true);
    setMicAudioWarning(false);
  };

  // System/Tab audio detection callbacks
  const handleSystemSilent = () => {
    console.log('System/Tab silent - no audio for 5 seconds');
    if (isRecording && !hasSystemDetectedRef.current) {
      setSystemAudioWarning(true);
    }
  };

  const handleSystemAudioDetected = () => {
    hasSystemDetectedRef.current = true;
    setSystemEverDetected(true);
    setSystemAudioWarning(false);
  };

  // Helper function to configure analyser
  const configureAnalyser = (analyserNode: AnalyserNode) => {
    /* eslint-disable no-param-reassign */
    analyserNode.fftSize = 2048;
    analyserNode.minDecibels = -100;
    analyserNode.maxDecibels = -30;
    analyserNode.smoothingTimeConstant = 0.8;
    /* eslint-enable no-param-reassign */
  };

  // Heartbeat interval to keep backend aware of ongoing upload
  useUploadPulse();

  useEffect(() => {
    const isActiveRecording = status === 'recording' || status === 'paused';

    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (isActiveRecording) {
        e.preventDefault();
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [status]);

  // Audio detection config (shared between effects)
  const audioDetectionConfig: AudioLevelDetectorConfig = useMemo(
    () => ({
      rmsThreshold: 0.02,
      rmsDbThreshold: -50,
      voiceEnergyThreshold: 0.1,
      silentDurationThreshold: 5000,
      sampleRate: 200,
    }),
    [],
  );

  // Ensure shared AudioContext exists
  const ensureAudioContext = () => {
    if (!audioContext.current || audioContext.current.state === 'closed') {
      audioContext.current = new (window.AudioContext ||
        (window as any).webkitAudioContext)();
    }
    return audioContext.current;
  };

  // System/display audio detection — detects audio from browser tab, window, or screen shares
  // Checks for actual audio tracks in the display stream regardless of display surface type
  useEffect(() => {
    if (!isRecording) {
      // Cleanup system detector on recording stop
      systemDetector.current?.stop();
      systemDetector.current = null;
      systemAnalyser.current = null;
      hasSystemDetectedRef.current = false;
      setSystemAudioWarning(false);
      setSystemEverDetected(false);
      setTabAudioActive(false);
      return undefined;
    }

    const setupSystemDetector = async () => {
      try {
        const hasDisplayAudioTracks =
          (streams.displayMediaStream?.getAudioTracks().length ?? 0) > 0;
        setTabAudioActive(hasDisplayAudioTracks);

        if (!hasDisplayAudioTracks) return;

        const ctx = ensureAudioContext();
        systemAnalyser.current = ctx.createAnalyser();
        configureAnalyser(systemAnalyser.current);

        const systemSource = ctx.createMediaStreamSource(
          streams.displayMediaStream,
        );
        systemSource.connect(systemAnalyser.current);

        systemDetector.current = new AudioLevelDetector(
          systemAnalyser.current,
          audioDetectionConfig,
          handleSystemSilent,
          handleSystemAudioDetected,
          () => {},
        );
        systemDetector.current.start();
        console.log('System/display audio detection started');
      } catch (error) {
        console.error('Error setting up system audio detection:', error);
      }
    };

    hasSystemDetectedRef.current = false;
    setSystemAudioWarning(false);
    setSystemEverDetected(false);
    setupSystemDetector();

    return () => {
      systemDetector.current?.stop();
      systemDetector.current = null;
      systemAnalyser.current = null;
    };
  }, [isRecording]);

  // Microphone audio detection — independent of system audio, handles mic toggle
  useEffect(() => {
    if (!isRecording) {
      // Cleanup mic detector on recording stop
      micDetector.current?.stop();
      micDetector.current = null;
      micAnalyser.current = null;
      hasMicDetectedRef.current = false;
      micDetectorSetUp.current = false;
      setMicAudioWarning(false);
      setMicEverDetected(false);
      return undefined;
    }

    const micDeviceId = selectedDevices?.audioinput?.deviceId;
    if (!microphoneAudio || !micDeviceId || micDeviceId === 'none') {
      // Mic was disabled or no device selected — stop mic detector but preserve state
      micDetector.current?.stop();
      micDetector.current = null;
      micAnalyser.current = null;
      micDetectorSetUp.current = false;
      // Don't reset micEverDetected — preserve it so re-enabling shows green
      setMicAudioWarning(false);
      return undefined;
    }

    let retryTimeout: ReturnType<typeof setTimeout> | null = null;
    let cancelled = false;

    const connectMicDetector = (ctx: AudioContext) => {
      micAnalyser.current = ctx.createAnalyser();
      configureAnalyser(micAnalyser.current);

      const micSource = ctx.createMediaStreamSource(streams.userMediaStream);
      micSource.connect(micAnalyser.current);

      micDetector.current = new AudioLevelDetector(
        micAnalyser.current,
        audioDetectionConfig,
        handleMicSilent,
        handleMicAudioDetected,
        () => {},
      );
      micDetector.current.start();
      micDetectorSetUp.current = true;
      console.log('Microphone audio detection started');
    };

    const setupMicDetector = async (attempt = 0) => {
      if (cancelled) return;
      try {
        const ctx = ensureAudioContext();

        if (streams.userMediaStream?.getAudioTracks().length > 0) {
          connectMicDetector(ctx);
        } else if (attempt < 3) {
          // Stream may not have audio tracks yet (e.g. mic enabled mid-recording)
          // Retry after a short delay to allow the stream to be ready
          console.log(
            `Microphone stream not ready, retrying (attempt ${attempt + 1}/3)`,
          );
          retryTimeout = setTimeout(() => setupMicDetector(attempt + 1), 500);
        } else {
          console.log('Microphone enabled but no audio tracks available');
          setMicAudioWarning(true);
        }
      } catch (error) {
        console.error('Error setting up microphone audio detection:', error);
      }
    };

    // Only reset detection ref if mic was never detected before
    // (preserve green state across mute/unmute cycles)
    if (!hasMicDetectedRef.current) {
      setMicAudioWarning(false);
    }
    setupMicDetector();

    return () => {
      cancelled = true;
      if (retryTimeout) clearTimeout(retryTimeout);
      micDetector.current?.stop();
      micDetector.current = null;
      micAnalyser.current = null;
    };
  }, [isRecording, microphoneAudio, selectedDevices?.audioinput?.deviceId]);

  // Analytics tracking on recording stop
  useEffect(() => {
    if (!isRecording) return undefined;

    recordingStartTime.current = Date.now();

    return () => {
      if (recordingStartTime.current) {
        const recordingDuration = Date.now() - recordingStartTime.current;
        const recordingDurationSeconds = recordingDuration / 1000;

        if (
          recordingDurationSeconds > 20 &&
          !hasMicDetectedRef.current &&
          !hasSystemDetectedRef.current
        ) {
          posthog.capture('recording-no-audio', {
            recordingDurationSeconds: Math.round(recordingDurationSeconds),
            recordingDurationMs: recordingDuration,
            micDetected: false,
            systemDetected: false,
            microphoneEnabled: !!microphoneAudio,
            systemAudioDetected: hasSystemDetectedRef.current,
            timestamp: new Date().toISOString(),
          });
          console.log(
            `PostHog: Tracked recording with no audio detected (${recordingDurationSeconds}s)`,
          );
        }
        recordingStartTime.current = null;
      }
    };
  }, [isRecording]);

  // Cleanup shared AudioContext when recording stops
  useEffect(() => {
    if (!isRecording) {
      if (audioContext.current?.state !== 'closed') {
        audioContext.current?.close();
      }
      audioContext.current = null;
    }
  }, [isRecording]);

  // Compute overall audio detection status for inline badge
  // This reflects which audio sources are actually working right now.
  const audioStatus: AudioStatus | null = useMemo(() => {
    if (!isRecording) return null;

    // Mic is considered active when mic setting is on AND a real device is selected
    const micDeviceSelected =
      selectedDevices?.audioinput?.deviceId &&
      selectedDevices.audioinput.deviceId !== 'none';
    const micActive = !!microphoneAudio && !!micDeviceSelected;

    // System/display audio is active when the display stream has audio tracks
    // (from browser tab sharing, or "Also share system audio" on window/screen)
    const tabActive = tabAudioActive;

    // No audio source is active — warn the user
    if (!micActive && !tabActive) return 'no-audio';

    // Check if each active source's detection phase has resolved
    const micResolved = !micActive || micEverDetected || micAudioWarning;
    const tabResolved = !tabActive || systemEverDetected || systemAudioWarning;

    // Still initializing if any active source hasn't resolved yet
    if (!micResolved || !tabResolved) return 'initializing';

    const micDetected = micActive && micEverDetected;
    const tabDetected = tabActive && systemEverDetected;

    // Both detected → fully connected
    if (micDetected && tabDetected) return 'connected';
    // Only one source exists and it's detected → connected
    if (micDetected && !tabActive) return 'connected';
    // Only mic detected (tab active but not detected) → partial
    if (micDetected && !tabDetected) return 'only-microphone';
    // Only system/tab audio detected → partial
    if (tabDetected && !micDetected) return 'only-tab';
    return 'no-audio';
  }, [
    isRecording,
    microphoneAudio,
    selectedDevices?.audioinput?.deviceId,
    tabAudioActive,
    micEverDetected,
    systemEverDetected,
    micAudioWarning,
    systemAudioWarning,
  ]);

  const handleToggleNotes = () => {
    setShowNotes((prev: boolean) => {
      const newValue = !prev;
      localStorage.setItem('notepad-visible', JSON.stringify(newValue));
      return newValue;
    });
  };

  const handleSwitchToPopup = () => {
    dispatch(setUIMode('popup'));
  };

  if (showDeviceSelection) {
    // If no screen or webcam is selected, record audio will start instantly
    if (!webCam && !screen) {
      return <SkipDeviceSelection />;
    }
    return <DeviceSelection />;
  }

  // Use the popup UI mode if selected
  if (uiMode === 'popup' && !showDeviceSelection) {
    return <PopupRecorder />;
  }

  return (
    <div
      className={cn(
        'fixed inset-0 flex items-start justify-center bg-background',
        isBackdropInvisible && !isSaving && 'invisible',
      )}
      style={{ zIndex: Z_INDEX.popover }}
    >
      <div
        className={cn(
          'relative flex flex-col w-full max-w-[600px] h-full p-2 transition-opacity',
          isBackdropInvisible && !isSaving ? 'opacity-0' : 'opacity-100',
        )}
      >
        {/* Minimize button - only shown when mounted from plugin */}
        {isMountedFromPlugin && (
          <div className="absolute top-2.5 right-2.5 z-10">
            <Button
              size="sm"
              variant="ghost"
              onClick={handleSwitchToPopup}
              className="h-8 w-8 p-0 bg-black/10 hover:bg-black/20"
            >
              <Minimize2 className="h-4 w-4" />
            </Button>
          </div>
        )}

        <RecorderControls
          onToggleNotes={handleToggleNotes}
          showNotes={showNotes}
          audioStatus={audioStatus}
        />

        <div
          className={cn(
            'w-full flex overflow-hidden transition-all duration-200',
            showNotes ? 'flex-1' : 'flex-none',
          )}
        >
          {showNotes && (
            <div className="flex-1 min-h-0">
              <Notepad />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default RecorderComponent;
